/**
 * AttackBadgeLayer — renders transient "−N" badges on both the attacker and
 * defender territory at the moment of each attack result, and a red damage
 * pulse on every territory that lost troops.
 *
 * The pulse replaced the old radial burst (four short lines in an X), which
 * read as clutter under the label. It flashes the territory's own shape red
 * once per roll, so the eye goes to where the troops were lost. A conquered
 * defender gets no pulse — ConquestFlashLayer owns the ownership change.
 *
 * Duration breakdown (full motion):
 *   0ms        Badges and the flash are AT full strength — no ramp.
 *   0–750ms    Hold (the flash ramps in over 60ms, then fades slowly to ~1060ms)
 *   750–1150ms Fade out (badges)
 *
 * Reduced motion: no pulse, badges fade 0→1 in 100ms, hold 600ms, fade
 * 1→0 in 100ms. Total 800ms.
 *
 * There is NO ramp-in, by the maintainer's call: a ramp is the only part of this
 * timeline that delays information — you cannot read dice that are still fading
 * up — and this result is the answer to a tap, so it appears at once. The hold
 * absorbs the change, so the total is unchanged, and the fade-OUT keeps its full
 * 400ms because it costs the viewer nothing: they have already read what is
 * leaving.
 *
 * Each label sits on the side of its troop anchor facing away from the other
 * territory, so the pair never stack on each other (attackBadgeSides). Labels
 * never leave the board: given the map's bounds, a label near the right edge
 * moves to the left of its troop anchor, and one near the top drops below it
 * (attackBadgePosition).
 *
 * The pulses and the labels render separately (`part`): MapView draws the
 * labels in their own overlay above the troop numbers, so a neighbour's count
 * can never cover a "−N", while the pulses stay beneath the numbers.
 *
 * Nothing gates input on this timeline — `canLaunch` in AttackUI checks only
 * `attack.isPending` — so a badge on screen never delays the next attack.
 *
 * pointerEvents="none" / accessibilityElementsHidden so touches pass through.
 */

import React from 'react'
import { Platform } from 'react-native'
import { Text as SvgText, Path, G } from 'react-native-svg'
import {
  useSharedValue,
  useAnimatedProps,
  withSequence,
  withTiming,
  Easing,
  createAnimatedComponent,
} from 'react-native-reanimated'
import { useReducedMotion } from 'react-native-reanimated'
import type { AttackAnimationEntry } from '../../hooks/useAttackEvents'
import { attackAnimationKey } from '../../lib/attackAnimationKey'
import { attackBadgePosition, attackBadgeSides, type BadgeBounds } from '../../lib/attackBadgePosition'
import { lightenHex } from '../../lib/colorAdjust'
import { damageFlashColor } from '../../lib/damageFlash'
import { heavyNumberFont } from '../../lib/heavyNumberFont'

const AnimatedText = createAnimatedComponent(SvgText)
const AnimatedPath = createAnimatedComponent(Path)

// ─── Damage pulse ─────────────────────────────────────────────────────────────

const DAMAGE_PULSE_PEAK = 0.5

/** Red flash over a territory that just lost troops: once per roll. */
function DamagePulse({ shape, color }: { shape: string; color: string }) {
  const pulse = useSharedValue(0)

  React.useEffect(() => {
    // One flash per roll: a quick 60ms ramp in (it answers a tap, so it must
    // land fast), then a slow, even fade. An ease-out decay lost most of it in
    // the first 200ms and read as too short. Done by ~1060ms, inside the
    // label's 1150ms.
    pulse.value = withSequence(
      withTiming(DAMAGE_PULSE_PEAK, { duration: 60, easing: Easing.out(Easing.quad) }),
      withTiming(0, { duration: 1000, easing: Easing.inOut(Easing.quad) }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const animatedProps = useAnimatedProps(() => ({ fillOpacity: pulse.value }))
  return <AnimatedPath d={shape} fill={color} fillOpacity={0} fillRule="evenodd"
    animatedProps={animatedProps} pointerEvents="none" />
}

// ─── Single side badge ────────────────────────────────────────────────────────

interface AttackBadgeProps {
  cx: number
  cy: number
  /** Horizontal offset direction, away from the other territory (attackBadgeSides). */
  side: -1 | 1
  lost: number
  color: string
  badgeFontSize: number
  reducedMotion: boolean
  bounds?: BadgeBounds
}

// Attack badges read at a glance against any territory fill — scale up
// slightly so the casualty count is unambiguous mid-fight. Affects only
// this layer; the territory troop-count badges keep the base size. (Was 1.3,
// which crowded close territories; maintainer, 2026-10-28.)
export const ATTACK_BADGE_SCALE = 1.15

function AttackBadge({
  cx,
  cy,
  side,
  lost,
  color,
  badgeFontSize: baseBadgeFontSize,
  reducedMotion,
  bounds,
}: AttackBadgeProps) {
  const badgeFontSize = baseBadgeFontSize * ATTACK_BADGE_SCALE
  const opacity = useSharedValue(0)

  React.useEffect(() => {
    if (reducedMotion) {
      opacity.value = withSequence(
        withTiming(1, { duration: 0 }),
        withTiming(1, { duration: 700 }),
        withTiming(0, { duration: 100 }),
      )
    } else {
      opacity.value = withSequence(
        withTiming(1, { duration: 0 }),
        withTiming(1, { duration: 750 }),
        withTiming(0, { duration: 400, easing: Easing.in(Easing.quad) }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const label = `−${lost}` // minus sign + N
  // Each label shifts away from the other territory — unless that would leave
  // the board.
  const { x: badgeCx, y: badgeCy } = attackBadgePosition(cx, cy, side, badgeFontSize, { label, bounds })

  const outlineAnimatedProps = useAnimatedProps(() => ({ opacity: opacity.value }))
  const fillAnimatedProps = useAnimatedProps(() => ({ opacity: opacity.value }))


  return (
    <G
      pointerEvents="none"
      {...(Platform.OS === 'ios' && { accessibilityElementsHidden: true })}
      {...(Platform.OS === 'android' && { importantForAccessibility: 'no-hide-descendants' as const })}
      {...(Platform.OS === 'web' && ({ 'aria-hidden': true } as { 'aria-hidden': true }))}
    >
      {/* Outline */}
      <AnimatedText
        x={badgeCx}
        y={badgeCy}
        fill="#000000"
        stroke="#000000"
        strokeWidth={badgeFontSize * 0.45}
        strokeLinejoin="round"
        fontSize={badgeFontSize}
        fontFamily={heavyNumberFont()}
        fontWeight="800"
        textAnchor="middle"
        animatedProps={outlineAnimatedProps}
      >
        {label}
      </AnimatedText>
      {/* Fill */}
      <AnimatedText
        x={badgeCx}
        y={badgeCy}
        fill={lightenHex(color, 0.45)}
        fontSize={badgeFontSize}
        fontFamily={heavyNumberFont()}
        fontWeight="800"
        textAnchor="middle"
        animatedProps={fillAnimatedProps}
      >
        {label}
      </AnimatedText>
    </G>
  )
}

// ─── Static (non-animated) variant ────────────────────────────────────────────

interface StaticAttackBadgeProps {
  cx: number
  cy: number
  side: -1 | 1
  lost: number
  color: string
  badgeFontSize: number
  bounds?: BadgeBounds
}

/**
 * Frozen attack badge — same outline + fill as `AttackBadge` but
 * locked at full opacity with no pop-in / fade-out / damage pulse.
 * Used by the time-machine snapshot view so the user can see what
 * the attack badge looked like at the moment of the action without
 * the animation timing out before they finish reading it.
 */
function StaticAttackBadge({
  cx,
  cy,
  side,
  lost,
  color,
  badgeFontSize: baseBadgeFontSize,
  bounds,
}: StaticAttackBadgeProps) {
  const badgeFontSize = baseBadgeFontSize * ATTACK_BADGE_SCALE
  const label = `−${lost}`
  const { x: badgeCx, y: badgeCy } = attackBadgePosition(cx, cy, side, badgeFontSize, { label, bounds })
  return (
    <G
      pointerEvents="none"
      {...(Platform.OS === 'ios' && { accessibilityElementsHidden: true })}
      {...(Platform.OS === 'android' && { importantForAccessibility: 'no-hide-descendants' as const })}
      {...(Platform.OS === 'web' && ({ 'aria-hidden': true } as { 'aria-hidden': true }))}
    >
      <SvgText
        x={badgeCx}
        y={badgeCy}
        fill="#000000"
        stroke="#000000"
        strokeWidth={badgeFontSize * 0.45}
        strokeLinejoin="round"
        fontSize={badgeFontSize}
        fontFamily={heavyNumberFont()}
        fontWeight="800"
        textAnchor="middle"
      >
        {label}
      </SvgText>
      <SvgText
        x={badgeCx}
        y={badgeCy}
        fill={lightenHex(color, 0.45)}
        fontSize={badgeFontSize}
        fontFamily={heavyNumberFont()}
        fontWeight="800"
        textAnchor="middle"
      >
        {label}
      </SvgText>
    </G>
  )
}

// ─── Layer ────────────────────────────────────────────────────────────────────

interface AttackBadgeLayerProps {
  entries: AttackAnimationEntry[]
  positionById: Record<string, { x: number; y: number } | undefined>
  badgeFontSize: number
  /**
   * Static (non-animated) attacker/defender badge — used by the
   * time-machine snapshot view to "freeze" the attack-result badge on
   * the from/to territories. Rendered alongside any animated entries.
   * The pair renders at full opacity, no pop-in / fade-out, no pulse.
   */
  staticEntry?: AttackAnimationEntry
  /** Territory shapes, for the damage pulse. */
  shapeById?: Record<string, string | undefined>
  /** The drawn map, so loss labels never render off the board. */
  bounds?: BadgeBounds
  /** Draw only the red pulses, only the "−N" labels, or both (default). */
  part?: 'all' | 'pulses' | 'labels'
}

export function AttackBadgeLayer({
  entries,
  positionById,
  badgeFontSize,
  staticEntry,
  shapeById,
  bounds,
  part = 'all',
}: AttackBadgeLayerProps) {
  const reducedMotion = useReducedMotion()
  const showPulses = part !== 'labels' && !reducedMotion
  const showLabels = part !== 'pulses'

  if (entries.length === 0 && (!staticEntry || !showLabels)) return null

  // Static snapshot badge — same visual treatment as the animated
  // badge but at full opacity, no animation, no pulse.
  const staticBadges = (() => {
    if (!staticEntry || !showLabels) return null
    const attackerPos = positionById[staticEntry.attackerTerritoryId]
    const defenderPos = positionById[staticEntry.defenderTerritoryId]
    const sides = attackBadgeSides(attackerPos, defenderPos)
    return (
      <G key={`attack-static-${staticEntry.attackerTerritoryId}-${staticEntry.defenderTerritoryId}`}>
        {attackerPos && staticEntry.attackerLost > 0 && (
          <StaticAttackBadge
            cx={attackerPos.x}
            cy={attackerPos.y}
            side={sides.attacker}
            lost={staticEntry.attackerLost}
            color={staticEntry.attackerColor}
            badgeFontSize={badgeFontSize}
            bounds={bounds}
          />
        )}
        {defenderPos && staticEntry.defenderLost > 0 && (
          <StaticAttackBadge
            cx={defenderPos.x}
            cy={defenderPos.y}
            side={sides.defender}
            lost={staticEntry.defenderLost}
            color={staticEntry.defenderColor}
            badgeFontSize={badgeFontSize}
            bounds={bounds}
          />
        )}
      </G>
    )
  })()

  return (
    <>
      {staticBadges}
      {entries.map((entry, index) => {
        const attackerPos = positionById[entry.attackerTerritoryId]
        const defenderPos = positionById[entry.defenderTerritoryId]
        const sides = attackBadgeSides(attackerPos, defenderPos)

        // Pulse every side that lost troops. A conquered defender is left to
        // ConquestFlashLayer, which owns the ownership change.
        const isLatest = index === entries.length - 1
        const attackerShape = entry.attackerLost > 0 ? shapeById?.[entry.attackerTerritoryId] : undefined
        const defenderShape = entry.defenderLost > 0 && !entry.conquered
          ? shapeById?.[entry.defenderTerritoryId] : undefined

        return (
          <G key={attackAnimationKey(entry, index)}>
            {/* Pulses first, so the labels sit on top of the red. Only the newest
                attack flashes: a new attack clears the previous flash and takes
                over (its labels stay until they fade). */}
            {showPulses && isLatest && attackerShape && <DamagePulse shape={attackerShape} color={damageFlashColor(entry.attackerColor)} />}
            {showPulses && isLatest && defenderShape && <DamagePulse shape={defenderShape} color={damageFlashColor(entry.defenderColor)} />}
            {showLabels && attackerPos && entry.attackerLost > 0 && (
              <AttackBadge
                cx={attackerPos.x}
                cy={attackerPos.y}
                side={sides.attacker}
                lost={entry.attackerLost}
                color={entry.attackerColor}
                badgeFontSize={badgeFontSize}
                reducedMotion={reducedMotion}
                bounds={bounds}
              />
            )}
            {showLabels && defenderPos && entry.defenderLost > 0 && (
              <AttackBadge
                cx={defenderPos.x}
                cy={defenderPos.y}
                side={sides.defender}
                lost={entry.defenderLost}
                color={entry.defenderColor}
                badgeFontSize={badgeFontSize}
                reducedMotion={reducedMotion}
                bounds={bounds}
              />
            )}
          </G>
        )
      })}
    </>
  )
}
